import { ArrowUpRight, Search } from 'lucide-react';
import { useState } from 'react';
import { useMap, useOverview } from '../api/hook.js';
import { MatrixRain } from '../component/MatrixRain.js';
import { Dot } from '../component/ui.js';
import { APP_MAP, logoUrl, type MapApp } from '../lib/appMap.js';
import { initials } from '../lib/format.js';

// Tutte le applicazioni dell'ecosistema in un unico punto, da aprire con un clic, sulla pioggia di Matrix.

// Il catalogo arriva da System; finché non risponde si usa la copia inclusa nel bundle.
const FALLBACK = APP_MAP.map((group) => ({ key: group.label, label: group.label, app: group.app }));
const host = (url: string) => new URL(url).host.replace(/^www\./, '');

function Logo({ app }: { app: MapApp }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className="map-logo" style={{ '--app': app.color } as React.CSSProperties}>
      {failed ? <b>{initials(app.name)}</b> : <img src={logoUrl(app.url)} alt="" loading="lazy" width={30} height={30} onError={() => setFailed(true)} />}
    </span>
  );
}

export function MapPage() {
  const overview = useOverview();
  const map = useMap();
  const catalog = map.data?.group ?? FALLBACK;
  const total = catalog.reduce((sum, group) => sum + group.app.length, 0);
  const [query, setQuery] = useState('');
  const state = new Map((overview.data?.application ?? []).map((item) => [item.id, item.state]));
  const needle = query.trim().toLowerCase();
  const groups = catalog
    .map((group) => ({
      ...group,
      app: needle ? group.app.filter((app) => `${app.name} ${app.description} ${app.url}`.toLowerCase().includes(needle)) : group.app,
    }))
    .filter((group) => group.app.length);

  return (
    <>
      <MatrixRain />
      <div className="page map">
        <header className="map-hero">
          <div>
            <h1>Map</h1>
            <p>{total} applicazioni dell’ecosistema in un unico punto: un clic e si aprono.</p>
          </div>
          <label className="map-search">
            <Search size={15} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Cerca un’applicazione" aria-label="Cerca un’applicazione" />
          </label>
        </header>

        {groups.length === 0 ? <div className="empty">Nessuna applicazione trovata.</div> : null}

        {groups.map((group) => (
          <section key={group.key} className="map-group">
            <h2>
              {group.label}
              <span>{group.app.length}</span>
            </h2>
            <div className="map-grid">
              {group.app.map((app) => {
                const health = state.get(app.id);
                return (
                  <a key={app.id} href={app.url} target="_blank" rel="noopener noreferrer" className="map-card" style={{ '--app': app.color } as React.CSSProperties}>
                    <div className="map-card-head">
                      <Logo app={app} />
                      <div className="map-card-title">
                        <strong>{app.name}</strong>
                        <span>{host(app.url)}</span>
                      </div>
                      <ArrowUpRight size={16} className="map-card-open" />
                    </div>
                    <p>{app.description}</p>
                    {health && health !== 'Unknown' ? (
                      <span className="map-card-state">
                        <Dot state={health} /> {health}
                      </span>
                    ) : null}
                  </a>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}
